/** @jsxImportSource @emotion/react */
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Select,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import { PrimitiveAtom, useAtom } from "jotai";
import { useRecoilValue } from "recoil";
import DeleteIcon from "../../../public/bulk-actions/DeleteIcon";
import { TagsAtom } from "../../atoms/TagsAtom";
import TagSelect from "../../components/TagSelect/TagSelect";
import { ItemType } from "../../pages/form-builder";
import { ITag } from "../../types/TagTypes";
import { nanoidForFooterAndAlias } from "../NewEmail/Modals/SavedSearch/FooterSearchModal";
import StyledInput from "../StyledInput";
import { Checkbox } from "@chakra-ui/react";
import { css } from "@emotion/react";

const AddButton = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #fb972e;
  cursor: pointer;
  transition: 0.2s ease-in-out;

  :hover {
    opacity: 0.8;
  }
`;

interface Props {
  itemAtom: PrimitiveAtom<ItemType>;
}

export interface ListOption {
  id: string;
  label: string;
  tagIds: string[];
  index: number;
}

export default function FormBuilderListSideBar(props: Props) {
  const [item, setItem] = useAtom(props.itemAtom);
  const tagsPool = useRecoilValue(TagsAtom);

  function handleOptionChange(option: ListOption) {
    const options = item.state?.options?.map((itemOption) => {
      if (itemOption.index === option.index) {
        return option;
      }
      return itemOption;
    });

    setItem({
      ...item,
      state: {
        ...item.state,
        options: options,
      },
    });
  }

  function handleOptionAdd() {
    setItem({
      ...item,
      state: {
        ...(item.state || {}),
        options: [
          ...(item.state?.options || []),
          {
            id: nanoidForFooterAndAlias(),
            label: "",
            tagIds: [],
            index: item.state?.options?.length || 0,
          },
        ],
      },
    });
  }

  function handleOptionDelete(option: ListOption) {
    const options = item.state?.options?.filter(
      (itemOption) => itemOption?.id !== option?.id
    );

    setItem({
      ...item,
      state: {
        ...item.state,
        options: options,
      },
    });
  }

  function handleChangeItemState(key: string, value: boolean | string) {
    setItem({
      ...item,
      state: {
        ...item.state,
        [key]: value,
      },
    });
  }

  return (
    <>
      <Accordion width="100%" allowMultiple>
        <AccordionItem>
          <AccordionButton
            _focus={{ outline: "none" }}
            className="accordion-button"
            padding="10px"
          >
            <div className="title-button">List</div>
            <AccordionIcon />
          </AccordionButton>

          <AccordionPanel borderBottom="1px solid #EBEBEB">
            <Checkbox
              onChange={(e) =>
                handleChangeItemState(
                  "allowMultipleSelections",
                  e.target.checked
                )
              }
              colorScheme="orange"
              css={css`
                margin: 10px 0 10px;

                width: 100%;
              `}
            >
              <span className="title-button">Allow multiple selections</span>
            </Checkbox>

            <hr
              css={css`
                width: calc(100% + 30px);
                margin-left: -15px;
              `}
            />

            <div>
              <span className="title-button">Align</span>
              <Select
                css={css`
                  margin-top: 5px;
                `}
                placeholder=""
                value={item.state?.align || "center"}
                onChange={(e) => {
                  handleChangeItemState("align", e.target.value);
                }}
              >
                <option value="flex-start">Left</option>
                <option value="center">Center</option>
                <option value="flex-end">Right</option>
              </Select>
            </div>

            <hr
              css={css`
                width: calc(100% + 30px);
                margin-left: -15px;
              `}
            />

            {item.state?.options?.map((option) => (
              <FormBuilderListCard
                key={option.id}
                tags={option.tagIds
                  .map((tagId) => {
                    const tag = tagsPool.find((tag) => tag._id === tagId);
                    return tag;
                  })
                  .filter((t) => t)}
                option={option}
                handleOptionChange={handleOptionChange}
                handleOptionDelete={handleOptionDelete}
              />
            ))}
            <AddButton onClick={handleOptionAdd}>
              + {!!item.state?.options?.length ? "Add another" : "Add option"}
            </AddButton>
          </AccordionPanel>
        </AccordionItem>
      </Accordion>
    </>
  );
}

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  grid-gap: 10px;
  margin-bottom: 10px;
`;
const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0 4px;

  svg {
    color: #707070;
    transition: 0.2s ease-in-out;
    cursor: pointer;

    :hover {
      color: #fb972e;
    }
  }

  > span {
    font-size: 15px;
    font-weight: 600;
    color: #3e3e3e;
  }
`;

interface CardProps {
  option: ListOption;
  tags: ITag[];
  handleOptionChange: (option: ListOption) => void;
  handleOptionDelete: (option: ListOption) => void;
}

function FormBuilderListCard(props: CardProps) {
  return (
    <CardContainer>
      <CardHeader>
        <span>Option {props.option.index + 1}</span>
        <div onClick={() => props.handleOptionDelete(props.option)}>
          <DeleteIcon />
        </div>
      </CardHeader>
      <StyledInput
        placeholder="Enter option text"
        defaultValue={props.option.label}
        onChange={(e) =>
          props.handleOptionChange({ ...props.option, label: e.target.value })
        }
      />
      <TagSelect
        handleChoseTags={(tags) => {
          props.handleOptionChange({
            ...props.option,
            tagIds: tags.map((tag) => tag._id),
          });
        }}
        tags={props.tags}
        cancelTagLoading={true}
      />
    </CardContainer>
  );
}
